import React, { useEffect, useState } from "react"
import { Button, Card, Collapse, Stack, Table } from "react-bootstrap"
import InfiniteScroll from "react-infinite-scroll-component"
import { Link } from "react-router-dom"
import { toast } from "react-toastify"
import recordNotFound from "../../assets/images/record_not_found.png"
import PageTitleButton from "../../components/PageTitleButton"
import SearchBox from "../../components/SearchBox"
import SweetAlerts from "../../components/SweetAlerts"
import TooltipComponent from "../../components/Tooltip"
import { admin } from "../../config/apiEndpoints"
import axiosProvider from "../../lib/AxiosProvider"
import {
  useHandleUnauthorizedAccess,
  HasModulePermission,
} from "../../lib/GlobalMethods"
import { modulePermission } from "../../lib/GlobalVariables"
import useDebounce from "../../lib/hooks/useDebounce"
import UserListSkeleton from "../../skeleton/users"

const Users = () => {
  const canDelete = HasModulePermission(modulePermission.user, "delete")

  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [hasMore, setHasMore] = useState(true)
  const [searchQuery, setSearchQuery] = useState("")
  const [show, setShow] = useState(false)
  const [showAlert, setShowAlert] = useState(false)
  const [isUpdate, setIsUpdate] = useState(false)
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: false,
  })
  const [recordsCount, setRecordsCount] = useState({
    total_records: false,
    active_records: false,
    archived_records: false,
  })

  const [open, setOpen] = useState(false)
  const [filterObj, setFilterObj] = useState({
    archived: false,
    active: false,
  })

  const [archived, setArchived] = useState(true)

  const { handleAccess } = useHandleUnauthorizedAccess()

  const debouncedSearchQuery = useDebounce(searchQuery, 500)

  const handleClose = () => {
    setShow(false)
    setIsUpdate(false)
    setShowAlert(false)
  }

  const fetchData = async (searchQuery) => {
    try {
      const response = await axiosProvider({
        method: "GET",
        endpoint: `${admin.endUserList}?page=${
          pagination.current_page
        }&search_term=${searchQuery ? searchQuery : ""}&archived=${
          filterObj.archived
        }&active=${filterObj.active}&limit=15`,
      })

      handleAccess({ code: response?.data?.code })

      const fetchedData = response?.data?.data || []
      setHasMore(true)

      response?.data?.code === 200 && setPagination(response?.data?.pagination)
      response?.data?.code === 200 &&
        setRecordsCount(response?.data?.record_counts)
      if (
        !fetchedData.length ||
        response.data.pagination.current_page ===
          response.data.pagination.total_pages
      ) {
        setHasMore(false)
      }

      setData((prevData) =>
        pagination.current_page === 1
          ? fetchedData
          : [...prevData, ...fetchedData]
      )

      setLoading(false)
    } catch (error) {
      setLoading(false)
      toast.error(error.message || "An error occurred")
    }
  }

  const handleArchive = async (id) => {
    try {
      const response = await axiosProvider({
        method: "DELETE",
        endpoint: archived
          ? admin.restoreEndUser + id
          : admin.deleteEndUser + id,
      })

      if (response?.data?.code === 200) {
        toast.success(response.data.message)

        setData((prevData) => {
          if (filterObj.archived === false && filterObj.active === false) {
            return data.map((user) => {
              if (user.id === id) {
                user.deletedAt = archived ? null : Date.now()
              }
              return user
            })
          } else {
            return data.filter((user) => user.id !== id)
          }
        })

        setRecordsCount((prev) => {
          if (archived) {
            return {
              ...prev,
              active_records: prev.active_records + 1,
              archived_records: prev.archived_records - 1,
            }
          } else {
            return {
              ...prev,
              active_records: prev.active_records - 1,
              archived_records: prev.archived_records + 1,
            }
          }
        })
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.error("Failed to delete user")
    } finally {
      setLoading(false)
    }
  }

  const fetchMoreData = () => {
    if (pagination.current_page < pagination.total_pages) {
      setPagination((prevState) => ({
        ...prevState,
        current_page: prevState.current_page + 1,
      }))
    } else {
      setHasMore(false)
    }
  }

  useEffect(() => {
    fetchData(debouncedSearchQuery)
  }, [debouncedSearchQuery, filterObj, pagination.current_page])

  const filterOnClick = () => {
    setOpen(!open)
  }

  return (
    <>
      <PageTitleButton
        PagetitleText={"Users"}
        filterShow={true}
        filterClick={filterOnClick}
        open={open}
      />
      <div className="details_cards_wrapper mb-3">
        <div
          className={`bg_sea_green details_card ${
            !filterObj.active && !filterObj.archived ? "card_active" : ""
          }`}
          onClick={() => {
            setFilterObj({ active: false, archived: false })
            setPagination({ current_page: 1 })
            setLoading(true)
          }}
        >
          <div className="card_titleicon">
            <h3 className="card_title">Total Users</h3>
            <span className="card_icon">
              <i className="icon_main total_users"></i>
            </span>
          </div>
          <p className="tls_numbers">{recordsCount.total_records || 0}</p>
        </div>
        <div
          className={`bg_light_green details_card ${
            filterObj.active && !filterObj.archived ? "card_active" : ""
          }`}
          onClick={() => {
            setFilterObj({ archived: false, active: true })
            setPagination({ current_page: 1 })
            setLoading(true)
          }}
        >
          <div className="card_titleicon">
            <h3 className="card_title">Active Users</h3>
            <span className="card_icon">
              <i className="icon_main activechecked"></i>
            </span>
          </div>
          <p className="tls_numbers">{recordsCount.active_records || 0}</p>
        </div>
        <div
          className={`bg_light_red details_card ${
            !filterObj.active && filterObj.archived ? "card_active" : ""
          }`}
          onClick={() => {
            setFilterObj({ archived: true, active: false })
            setPagination({ current_page: 1 })
            setLoading(true)
          }}
        >
          <div className="card_titleicon">
            <h3 className="card_title">Archive Users</h3>
            <span className="card_icon">
              <i className="icon_main archive_data"></i>
            </span>
          </div>
          <p className="tls_numbers">{recordsCount.archived_records || 0}</p>
        </div>
      </div>

      <Collapse in={open}>
        <div id="brand-filter-collapse" className="filters_sticky">
          <Card className="mb-3">
            <Card.Body>
              <Stack direction="horizontal" gap={2} className="flex-wrap">
                <div className="d-flex gap-2 align-items-center justify-content-between flex-wrap">
                  <div className="d-inline-flex gap-2 align-items-center">
                    <SearchBox
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e?.target?.value)
                        setPagination({ current_page: 1 })
                        setLoading(true)
                      }}
                    />
                  </div>
                </div>
              </Stack>
            </Card.Body>
          </Card>
        </div>
      </Collapse>

      {!loading ? (
        <Card>
          <Card.Body>
            <InfiniteScroll
              dataLength={data?.length || 10}
              next={fetchMoreData}
              hasMore={hasMore}
              loader={<UserListSkeleton infiniteScroll={true} rows={1} />}
            >
              <Table className="rounded mb-0 table-striped" responsive="md">
                <thead>
                  <tr>
                    <th className="text-center">Mobile</th>
                    <th className="text-center">Name</th>
                    <th className="text-center">Email</th>
                    <th className="text-center">Bookings</th>
                    <th className="text-center">No of Vehicles</th>
                    {canDelete && <th className="text-center">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {data.map((item, index) => (
                    <tr key={index + 1}>
                      <td className="text-center">
                        <div>{item?.phone ? item?.phone : "-"}</div>
                      </td>
                      <td className="text-center">
                        <Link
                          to={`/user-details/${item.id}`}
                          className="text-decoration-none"
                        >
                          <div className="station_nmaddress">
                            <p className="station_nm">
                              {item?.name ? item?.name : "-"}
                            </p>
                          </div>
                        </Link>
                      </td>
                      <td className="text-center">
                        {item?.email ? item?.email : "-"}
                      </td>

                      <td className="text-center">{item?.booking_count}</td>
                      <td className="text-center">
                        {item?.vehicles_catalogues?.length}
                      </td>
                      {canDelete && (
                        <td className="text-center">
                          {item?.deletedAt === null ? (
                            <TooltipComponent
                              toolplace="top"
                              tooltipText="Archived"
                            >
                              <Button
                                variant={`btnarchive_icon`}
                                className="btn_icon"
                                onClick={() => {
                                  setArchived(false)
                                  setShowAlert(true)
                                  setIsUpdate(item)
                                }}
                              >
                                <i className="icon_main archive_data"></i>
                              </Button>
                            </TooltipComponent>
                          ) : (
                            <TooltipComponent
                              toolplace="top"
                              tooltipText="Restore"
                            >
                              <Button
                                variant={`btnrestore_icon`}
                                className="btn_icon"
                                onClick={() => {
                                  setArchived(true)
                                  setShowAlert(true)
                                  setIsUpdate(item)
                                }}
                              >
                                <i className="icon_main restore_data"></i>
                              </Button>
                            </TooltipComponent>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                  {data && !data?.length && (
                    <tr>
                      <td className="text-center py-4" colSpan={5}>
                        <div className="norecord_wrapper">
                          <img src={recordNotFound} alt="No Record Found" />
                          <p className="norecords_title">No Records Found</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </InfiniteScroll>
          </Card.Body>
        </Card>
      ) : (
        <UserListSkeleton />
      )}

      <SweetAlerts
        showAlert={showAlert}
        alertMessage={`Are you sure you want to ${
          archived ? "Restore" : "Archive"
        } this User ?`}
        handleClose={handleClose}
        iconClassName={`${
          archived ? "restore_data bg-black" : "archive_data bg-warning"
        }`}
        handleAlert={() => {
          handleArchive(isUpdate.id)
        }}
      />
    </>
  )
}

export default Users
